import { X, Activity, DollarSign, Eye, MousePointer, Target, Zap, Globe, BarChart3, Percent, Award } from 'lucide-react';
import { fmt } from '../utils/helpers';

interface DomainDetailModalProps {
    domain: any;
    onClose: () => void;
}

export function DomainDetailModal({ domain, onClose }: DomainDetailModalProps) {
    if (!domain) return null;

    // Calcular fill rate
    const fillRate = domain.impressions > 0
        ? ((domain.impressions / (domain.impressions + domain.unfilled_impressions)) * 100)
        : 0;

    // Filtrar países ativos
    const activeCountries = domain.countries?.filter((c: any) => c.impressions > 0) || [];

    // Função para determinar tipo e cor do ad block - versão clean
    const getBlockStyle = (blockName: string) => {
        const name = blockName.toLowerCase();
        if (name.includes('interstitial')) return {
            label: 'Interstitial',
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            iconBg: 'bg-purple-100',
            iconColor: 'text-purple-600',
            icon: Eye
        };
        if (name.includes('rewarded')) return {
            label: 'Rewarded',
            bg: 'bg-yellow-50',
            border: 'border-yellow-200',
            iconBg: 'bg-yellow-100',
            iconColor: 'text-yellow-600',
            icon: Award
        };
        if (name.includes('trigger')) return {
            label: 'Trigger',
            bg: 'bg-purple-100',
            border: 'border-purple-200',
            iconBg: 'bg-purple-100',
            iconColor: 'text-purple-600',
            icon: Eye
        };
        if (name.includes('anchor') || name.includes('topo')) return {
            label: 'Anchor',
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            icon: MousePointer
        };
        if (name.includes('content')) return {
            label: 'Content',
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            iconBg: 'bg-emerald-100',
            iconColor: 'text-emerald-600',
            icon: Activity
        };
        return {
            label: 'Other',
            bg: 'bg-gray-50',
            border: 'border-gray-200',
            iconBg: 'bg-gray-100',
            iconColor: 'text-gray-600',
            icon: Target
        };
    };


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
                {/* Header Clean */}
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-8 py-6 border-b-2 border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center border-2 border-indigo-200">
                                    <Globe className="h-8 w-8 text-indigo-600" />
                                </div>
                            </div>

                            {/* Info */}
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-1">{domain.domain}</h2>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                        <Zap className="h-3.5 w-3.5 text-indigo-600" />
                                        {domain.ad_blocks?.length || 0} Ad Blocks
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="flex items-center gap-1">
                                        <Globe className="h-3.5 w-3.5 text-emerald-600" />
                                        {activeCountries.length} Países
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="flex items-center gap-1">
                                        <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                                        Fill Rate: <span className="font-semibold text-gray-900">{fillRate.toFixed(1)}%</span>
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="flex items-center gap-1">
                                        <Activity className="h-3.5 w-3.5 text-cyan-600" />
                                        Viewability: <span className="font-semibold text-gray-900">{fmt.percent(domain.active_view)}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-all"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50">
                    <div className="p-8">
                        {/* KPIs Grid - Clean */}
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                            {/* Receita */}
                            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <DollarSign className="h-4 w-4 text-emerald-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Receita</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 mb-1">{fmt.currency(domain.revenue)}</p>
                                <p className="text-xs text-gray-500">Cliente: {fmt.currency(domain.revenue_client)}</p>
                            </div>

                            {/* Impressões */}
                            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Eye className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Impressões</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 mb-1">{fmt.number(domain.impressions)}</p>
                                <p className="text-xs text-gray-500">Fill: {fillRate.toFixed(1)}%</p>
                            </div>

                            {/* CTR */}
                            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <Percent className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">CTR</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 mb-1">{fmt.percent(domain.ctr)}</p>
                                <p className="text-xs text-gray-500">{fmt.number(domain.clicks)} cliques</p>
                            </div>

                            {/* eCPM */}
                            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-orange-100 rounded-lg">
                                        <Target className="h-4 w-4 text-orange-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">eCPM</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 mb-1">{fmt.currency(domain.ecpm)}</p>
                                <p className="text-xs text-gray-500">PMR: {fmt.percent(domain.pmr)}</p>
                            </div>

                            {/* Viewability */}
                            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="p-2 bg-cyan-100 rounded-lg">
                                        <Activity className="h-4 w-4 text-cyan-600" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Viewability</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 mb-1">{fmt.percent(domain.active_view)}</p>
                                <p className="text-xs text-gray-500">{fmt.number(domain.viewable_impressions)} viewable</p>
                            </div>
                        </div>

                        {/* Layout em 2 Colunas */}
                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* Coluna Principal - Ad Blocks */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Ad Blocks */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                            <Zap className="h-6 w-6 text-indigo-600" />
                                            Ad Blocks Performance
                                        </h3>
                                        <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">
                                            {domain.ad_blocks?.length || 0} blocos
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        {domain.ad_blocks?.map((block: any, idx: number) => {
                                            const style = getBlockStyle(block.block);
                                            const BlockIcon = style.icon;
                                            const blockFillRate = block.impressions > 0
                                                ? ((block.impressions / (block.impressions + block.unfilled_impressions)) * 100)
                                                : 0;

                                            return (
                                                <div
                                                    key={idx}
                                                    className={`${style.bg} border-2 ${style.border} rounded-xl p-5 hover:shadow-md transition-all`}
                                                >
                                                    <div className="flex items-start gap-4 mb-4">
                                                        {/* Icon & Badge */}
                                                        <div className="flex-shrink-0">
                                                            <div className={`w-12 h-12 ${style.iconBg} rounded-lg flex items-center justify-center`}>
                                                                <BlockIcon className={`h-6 w-6 ${style.iconColor}`} />
                                                            </div>
                                                        </div>

                                                        {/* Info */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className={`px-2 py-0.5 ${style.iconBg} ${style.iconColor} text-xs font-semibold rounded`}>
                                                                    {style.label}
                                                                </span>
                                                                <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>
                                                            </div>
                                                            <h4 className="font-bold text-gray-900 text-sm mb-1 truncate" title={block.block}>
                                                                {block.block}
                                                            </h4>
                                                            <p className="text-2xl font-bold text-gray-900">{fmt.currency(block.revenue)}</p>
                                                        </div>
                                                    </div>

                                                    {/* Métricas Grid */}
                                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                                                        <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <Eye className="h-3 w-3 text-blue-600" />
                                                                <span className="text-xs text-gray-600 font-medium">Impressões</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-gray-900">{fmt.number(block.impressions)}</p>
                                                        </div>

                                                        <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <MousePointer className="h-3 w-3 text-purple-600" />
                                                                <span className="text-xs text-gray-600 font-medium">Cliques</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-gray-900">{fmt.number(block.clicks)}</p>
                                                        </div>

                                                        <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <Percent className="h-3 w-3 text-orange-600" />
                                                                <span className="text-xs text-gray-600 font-medium">CTR</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-orange-600">{fmt.percent(block.ctr)}</p>
                                                        </div>

                                                        <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <DollarSign className="h-3 w-3 text-emerald-600" />
                                                                <span className="text-xs text-gray-600 font-medium">eCPM</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-emerald-600">{fmt.currency(block.ecpm)}</p>
                                                        </div>

                                                        <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <Activity className="h-3 w-3 text-cyan-600" />
                                                                <span className="text-xs text-gray-600 font-medium">Viewability</span>
                                                            </div>
                                                            <p className="text-sm font-bold text-cyan-600">{fmt.percent(block.active_view)}</p>
                                                        </div>
                                                    </div>

                                                    {/* Footer Stats */}
                                                    <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-200">
                                                        <div className="flex gap-4">
                                                            <span className="text-gray-600">
                                                                PMR: <span className="font-bold text-gray-900">{fmt.percent(block.pmr)}</span>
                                                            </span>
                                                            <span className="text-gray-600">
                                                                Fill: <span className="font-bold text-emerald-600">{blockFillRate.toFixed(1)}%</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar - Países e Stats */}
                            <div className="space-y-6">
                                {/* Métricas Avançadas */}
                                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <BarChart3 className="h-5 w-5 text-gray-600" />
                                        Métricas Avançadas
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-600 mb-1">Req. Servidas</p>
                                            <p className="text-lg font-bold text-gray-900">{fmt.number(domain.requests_served)}</p>
                                        </div>
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-600 mb-1">Req. Elegíveis</p>
                                            <p className="text-lg font-bold text-gray-900">{fmt.number(domain.elegible_ad_request)}</p>
                                        </div>
                                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                            <p className="text-xs text-orange-600 mb-1 font-medium">Não Preenchidas</p>
                                            <p className="text-lg font-bold text-orange-600">{fmt.number(domain.unfilled_impressions)}</p>
                                        </div>
                                        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                            <p className="text-xs text-emerald-600 mb-1 font-medium">Fill Rate</p>
                                            <p className="text-lg font-bold text-emerald-600">{fillRate.toFixed(1)}%</p>
                                        </div>
                                        <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                                            <p className="text-xs text-cyan-600 mb-1 font-medium">Viewability</p>
                                            <p className="text-lg font-bold text-cyan-600">{fmt.percent(domain.active_view)}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {fmt.number(domain.viewable_impressions)} / {fmt.number(domain.measurable_impressions)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Top 5 Países */}
                                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                            <Globe className="h-5 w-5 text-emerald-600" />
                                            Top Países
                                        </h3>
                                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                                            {activeCountries.length}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        {activeCountries.slice(0, 5).map((country: any, idx: number) => (
                                            <div key={idx} className="group p-3 bg-gray-50 rounded-lg hover:bg-emerald-50 hover:border-emerald-200 border border-gray-200 transition-all">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs ${
                                                            idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                                                                idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700' :
                                                                    idx === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400 text-white' :
                                                                        'bg-gray-200 text-gray-600'
                                                        }`}>
                                                            {idx + 1}
                                                        </div>
                                                        <span className="font-bold text-gray-900 text-sm">{country.country}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-lg font-bold text-emerald-600">{fmt.currency(country.revenue)}</p>
                                                        <p className="text-xs text-gray-500">Receita</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-orange-500">{fmt.currency(country.ecpm)}</p>
                                                        <p className="text-xs text-gray-500">eCPM</p>
                                                    </div>
                                                </div>                                                <div className="flex items-center justify-between text-xs text-gray-600">
                                                    <span>{fmt.number(country.impressions)} imp</span>
                                                    <span className="font-semibold text-cyan-600">{fmt.percent(country.active_view)} view</span>
                                                    <span className="font-semibold text-purple-600">{fmt.percent(country.ctr)} CTR</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {activeCountries.length > 5 && (
                                        <div className="mt-3 pt-3 border-t border-gray-200 text-center">
                                            <span className="text-xs text-gray-500 font-medium">
                                                +{activeCountries.length - 5} outros países
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}